import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatDateTime, formatIDR } from "@/lib/format";
import { Card, PageHeader, Badge } from "@/components/ui-kit";
import { usePagination } from "@/hooks/usePagination";
import { Bell, AlertTriangle, TrendingUp, TrendingDown, Minus, FileText, Truck, ChevronLeft, Filter } from "lucide-react";
import { Link } from "react-router-dom";

const PRICE_FIELD_LABELS = {
  price_non_vat: "Harga Non PPN",
  price_ex_vat: "Harga Exc PPN",
  price_with_vat: "Harga + PPN",
  delivery_cost: "Biaya Kirim",
  unloading_fee: "Biaya Bongkar",
};

function NotifPriceChange({ detail }) {
  if (!detail || typeof detail !== "object") return null;
  return (
    <div className="mt-1 space-y-0.5">
      {Object.entries(detail).map(([field, ch]) => {
        const o = Number(ch.old) || 0;
        const n = Number(ch.new) || 0;
        const diff = Number(ch.diff ?? (n - o));
        const pct = ch.pct != null ? Number(ch.pct) : (o ? (diff / o * 100) : null);
        const dir = ch.direction || (diff > 0 ? "naik" : diff < 0 ? "turun" : "tetap");
        const tone = dir === "naik" ? "text-emerald-700" : dir === "turun" ? "text-red-700" : "text-stone-500";
        const Icon = dir === "naik" ? TrendingUp : dir === "turun" ? TrendingDown : Minus;
        return (
          <div key={field} className={`flex flex-wrap items-center gap-1.5 text-[11px] ${tone}`}>
            <Icon className="h-3 w-3" />
            <span className="font-semibold uppercase">{dir}</span>
            <span className="text-stone-700">{PRICE_FIELD_LABELS[field] || field}:</span>
            <span className="font-mono">{formatIDR(o)} → {formatIDR(n)}</span>
            {diff !== 0 && (<span className="font-mono">({diff > 0 ? "+" : ""}{formatIDR(diff)}{pct != null ? ` · ${diff > 0 ? "+" : ""}${pct.toFixed(2)}%` : ""})</span>)}
          </div>
        );
      })}
    </div>
  );
}

function alertIcon(t) {
  if (t === "low_stock" || t === "cs_escalation") return <AlertTriangle className="h-3.5 w-3.5 text-red-600" />;
  if (t === "po_new") return <FileText className="h-3.5 w-3.5 text-blue-600" />;
  if (t === "sj_new") return <Truck className="h-3.5 w-3.5 text-emerald-600" />;
  if (t === "invoice_overdue") return <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />;
  return <Bell className="h-3.5 w-3.5 text-amber-600" />;
}

/**
 * ClientNotifications — full listing of klien notifications & warnings (Item 2 iter v19).
 * Filters by category (notification | warning | all). Pagination 14/page.
 */
export default function ClientNotifications() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all"); // all | notification | warning
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/client/notifications", { params: { include_read: true } })
      .then((r) => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return rows;
    return rows.filter((a) => (a.category || "notification") === filter);
  }, [rows, filter]);

  const { pageItems, Pagination } = usePagination(filtered, { perPage: 14 });

  const cnt = useMemo(() => ({
    all: rows.length,
    notification: rows.filter((a) => (a.category || "notification") === "notification").length,
    warning: rows.filter((a) => a.category === "warning").length,
  }), [rows]);

  return (
    <div className="min-h-full bg-[#fafaf5]">
    <div className="max-w-[1100px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
      <Link to="/client" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-stone-500 hover:text-blue-700">
        <ChevronLeft className="h-3 w-3" /> Beranda
      </Link>
      <PageHeader
        overline={`Klien · ${user?.client_name || "—"}`}
        title="Semua Notifikasi & Peringatan"
        description="Riwayat lengkap update dan peringatan yang terkait dengan akun perusahaan Anda."
      />
      {/* Filter chips */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <Filter className="h-3.5 w-3.5 text-stone-400 mr-1" />
        {[
          ["all", `Semua (${cnt.all})`],
          ["notification", `Notifikasi (${cnt.notification})`],
          ["warning", `Peringatan (${cnt.warning})`],
        ].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`text-xs uppercase tracking-wider px-3 py-1.5 border transition-colors ${filter === k ? "bg-terra-500 text-white border-terra-500" : "bg-white text-stone-600 border-stone-200 hover:border-stone-400"}`}
            data-testid={`client-notif-filter-${k}`}
          >
            {label}
          </button>
        ))}
      </div>
      <Card className="p-0" data-testid="client-notif-list">
        {loading ? (
          <p className="px-5 py-12 text-center text-sm text-stone-400">Memuat…</p>
        ) : filtered.length === 0 ? (
          <p className="px-5 py-12 text-center text-sm text-stone-400">Tidak ada notifikasi.</p>
        ) : (
          <div className="divide-y divide-stone-100">
            {pageItems.map((a) => {
              const isWarning = a.category === "warning";
              return (
                <div key={a.id} className={`px-5 py-3 flex items-start gap-3 ${a.read ? "opacity-60" : ""}`} data-testid={`client-notif-row-${a.id}`}>
                  <div className="mt-0.5">{alertIcon(a.type)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-stone-900">{a.title}</p>
                      {isWarning ? <Badge tone="danger">Peringatan</Badge> : <Badge tone="info">Notifikasi</Badge>}
                      {a.read && <Badge tone="stone">Sudah dibaca</Badge>}
                    </div>
                    {a.type === "price_change" && <NotifPriceChange detail={a.detail} />}
                    {a.type === "invoice_overdue" && a.detail && (
                      <p className="text-[11px] text-stone-600 mt-1">
                        Sisa tagihan: <span className="font-mono font-semibold">{formatIDR(a.detail.balance_due)}</span> · Jatuh tempo: <span className="font-mono">{a.detail.due_date}</span>
                      </p>
                    )}
                    <p className="text-[10px] text-stone-400 font-mono mt-1">{formatDateTime(a.ts)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
      {Pagination}
    </div>
    </div>
  );
}
